import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AssignmentPickList from '../../../components/ui/AssignmentPickList'
import FormDialog from '../../../components/ui/FormDialog'
import EmptyState from '../../../components/ui/EmptyState'
import { getUserRoles } from '../api/usersApi'
import type { Role, UserSummary } from '../types'

interface Props {
  visible: boolean
  user: UserSummary | null
  roleOptions: Role[]
  submitting: boolean
  onSubmit: (roleIds: number[]) => void
  onHide: () => void
}

/** Kullanıcıya rol atama diyaloğu (set semantiği). Açılışta mevcut roller yüklenir; MultiSelect. */
export default function UserRolesDialog({
  visible,
  user,
  roleOptions,
  submitting,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t, i18n } = useTranslation()
  const [selected, setSelected] = useState<number[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (visible && user) {
      setSelected([])
      setLoadError(false)
      setLoading(true)
      getUserRoles(user.id)
        .then((ids) => {
          if (!cancelled) setSelected(ids)
        })
        .catch(() => {
          if (!cancelled) setLoadError(true)
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }
    return () => {
      cancelled = true
    }
  }, [visible, user])

  const en = i18n.language.startsWith('en')
  const items = roleOptions.map((r) => ({ id: r.id, label: en && r.nameEn ? r.nameEn : r.nameTr }))

  // Başlığın yanında hangi kullanıcıya rol atandığı görünsün: "Ad Soyad (kullanıcı adı)".
  const userLabel = user
    ? [`${user.name} ${user.surname}`.trim(), user.userName ? `(${user.userName})` : '']
        .filter(Boolean)
        .join(' ')
    : ''

  return (
    <FormDialog
      visible={visible}
      width="72rem"
      header={t('admin.users.rolesDialog.title')}
      headerContext={userLabel}
      submitLabel={t('admin.users.rolesDialog.save')}
      submitting={submitting || loading}
      // Yükleme hatasında kaydetme kilitli — aksi halde boş seçim tüm rolleri siler (veri kaybı).
      submitDisabled={loading || loadError}
      onSubmit={() => onSubmit(selected)}
      onHide={onHide}
    >
      <div className="admin-form">
        {loadError ? (
          <EmptyState title={t('admin.users.rolesDialog.loadError')} />
        ) : items.length === 0 && !loading ? (
          <EmptyState title={t('admin.users.rolesDialog.empty')} />
        ) : (
          <AssignmentPickList
            items={items}
            value={selected}
            onChange={setSelected}
            sourceHeader={t('admin.users.rolesDialog.sourceHeader')}
            targetHeader={t('admin.users.rolesDialog.targetHeader')}
            filterPlaceholder={t('common.searchPlaceholder')}
          />
        )}
      </div>
    </FormDialog>
  )
}
